import {
  Accordion as MuiAccordion,
  AccordionSummary as MuiAccordionSummary,
  AccordionDetails as MuiAccordionDetails,
  styled,
  AccordionProps as MuiAccordionProps,
  Theme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ReactNode, useState } from 'react';
import { MUIStyledCommonProps } from '@mui/system';

export const StyledAccordion = styled(MuiAccordion)(({ theme }) => ({
  '&.MuiAccordion-root': {
    overflow: 'hidden',
    boxShadow: 'none',
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 12,
    cursor: 'auto',
    transition: '0.3s',
    marginBottom: theme.spacing(2),

    '&.Mui-expanded': {
      marginTop: 0,
    },

    '&::before': {
      content: 'none',
    },

    '&.inactive': {
      color: theme.palette.grey[500],

      '.actions svg, .action p': {
        color: theme.palette.grey[400],
      },
    },

    '.MuiButtonBase-root.MuiAccordionSummary-root': {
      cursor: 'pointer',
    },
  },
}));
//export this if you want to styled it differently
const AccordionSummary = styled(MuiAccordionSummary)(({ theme }) => ({
  fontFamily: 'Epilogue',
  fontSize: '0.875rem',
  color: theme.palette.grey[700],
  '.MuiTypography-root': {
    fontFamily: 'Epilogue',
    fontSize: '0.875rem',
  },
}));
export const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  fontFamily: 'Epilogue',
  fontSize: '0.875rem',
  color: theme.palette.grey[700],
  '.MuiTypography-root': {
    fontFamily: 'Epilogue',
    fontSize: '0.875rem',
    marginBottom: theme.spacing(1),
  },
}));

export type AccordionProps = Omit<MuiAccordionProps, 'children'> & {
  name: string;
  expanded?: boolean;
  expandIcon?: ReactNode;
  handleChange?: (name: string, expanded: boolean) => void;
  summary: string | JSX.Element;
  details: string | JSX.Element;
  summaryComponent?: React.FC;
  detailsComponent?: React.FC;
  accordionComponent?: React.FC<
    MuiAccordionProps & MUIStyledCommonProps<Theme>
  >;
  hideSummaryWhenExpanded?: boolean;
  hideDetailsWhenCollapsed?: boolean;
};

const Accordion: React.FC<AccordionProps> = ({
  expanded,
  name,
  summary,
  details,
  expandIcon = <ExpandMoreIcon />,
  handleChange = () => null,
  summaryComponent = AccordionSummary,
  detailsComponent = AccordionDetails,
  accordionComponent = StyledAccordion,
  hideSummaryWhenExpanded,
  hideDetailsWhenCollapsed,
  ...props
}) => {
  const Accordion = accordionComponent;
  const Summary = summaryComponent;
  const Details = detailsComponent;
  const isControlled = expanded !== undefined;
  const [accordionExpanded, setAccordionExpanded] = useState(
    props.defaultExpanded,
  );

  const isExpanded = isControlled ? expanded : accordionExpanded;

  const onChange = (event: React.SyntheticEvent, newExpanded: boolean) => {
    if (!isControlled) {
      setAccordionExpanded(newExpanded);
    }
    handleChange(name, newExpanded);
  };

  return (
    <Accordion expanded={isExpanded} onChange={onChange} {...props}>
      <Summary expandIcon={expandIcon}>
        {hideSummaryWhenExpanded && isExpanded ? '' : summary}
      </Summary>
      <Details>
        {hideDetailsWhenCollapsed && !isExpanded ? '' : details}
      </Details>
    </Accordion>
  );
};

export default Accordion;
